import React, { memo, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Logger, useDetectScroll } from '@saviynt/common';
import { Box, Button, ModalIsland } from '@saviynt/design-system';

import { MIN_COMMENT_LENGTH } from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
} from '../../../../../shared/hooks';
import { type NavigateToDetailsProps } from '../../../../../shared/hooks';
import {
  type ListRowAccountState,
  type TrackedFunctionData,
  type ViolationsActionManagerState,
} from '../../../../../shared/types';
import { detailsActions } from '../../../../Details';
import { type FunctionAnalysisState } from '../../../data-layer/violationsTabTypes';

import RemediationCommentScreen from './RemediationCommentScreen';
import RemediationFooter from './RemediationFooter';
import RemediationFunctionScreen from './RemediationFunctionScreen';
import RemediationHeader from './RemediationHeader';
import RemediationImpactScreen from './RemediationImpactScreen';

import './ViolationsRemediateAction.css';

export const REMEDIATE_MODE = {
  COM_ASG_ENTS: 'common_assigned_entitlements_for_one_or_many_functions',
  MUL_FUN_ENTS: 'multiple_assigned_entitlements_for_many_functions',
};

export type ActiveScreenType = 1 | 2 | 3 | 4;
export type RemediateMode =
  (typeof REMEDIATE_MODE)[keyof typeof REMEDIATE_MODE];

interface ViolationsRemediateActionProps {
  sodKey: string;
  isOpen: boolean;
  isLoading: boolean;
  subtitlelinkText: string;
  userName: string;
  displayName: string;
  isAllowedSaveSimulation?: boolean;
  functions?: TrackedFunctionData[];
  accounts?: ListRowAccountState[];
  isDisabled?: boolean;
  redirectTo?: NavigateToDetailsProps;
  handleSumbit: (
    comment: string,
    configs?: {
      functionId: number;
      violationId: string;
      remediateOption: string;
    }
  ) => void;
  handleModalClose: () => void;
  isDetailsPage?: boolean;
  violationActionManager: ViolationsActionManagerState | null;
}

/**
 * ViolationsRemediateAction
 */
function ViolationsRemediateAction({
  sodKey,
  isOpen,
  isDisabled,
  isLoading,
  isAllowedSaveSimulation,
  userName,
  displayName,
  subtitlelinkText,
  functions,
  accounts,
  redirectTo,
  handleSumbit,
  handleModalClose,
  isDetailsPage,
  violationActionManager,
}: ViolationsRemediateActionProps) {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const { navigateToDetails } = useNavigateTo();
  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  // Hooks
  const [activeScreen, setActiveScreen] = useState<ActiveScreenType>(1);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [fnKeys, setFnKeys] = useState<string[] | null>(null);
  const [isSectionError, setIsSectionError] = useState(false);
  const [functionAnalysis, setFunctionAnalysis] =
    useState<FunctionAnalysisState | null>(null);
  // Comment Screen
  const [commentValue, setCommentValue] = useState('');
  const [isCommentCritical, setIsCommentCritical] = useState(false);
  const [remediateValue, setRemediateValue] = useState<string>('');

  const isRequest = Boolean(remediateValue === 'CREATE_REQUEST');

  // Uilities
  const onRedirect = useCallback(() => {
    if (redirectTo && !isDetailsPage && violationActionManager !== null) {
      dispatch(
        detailsActions.setViolationTabdetailsActionManager({
          ...violationActionManager,
          isOpen: false,
        })
      );

      handleModalClose();
      navigateToDetails(redirectTo);
    }
  }, [
    dispatch,
    handleModalClose,
    isDetailsPage,
    navigateToDetails,
    redirectTo,
    violationActionManager,
  ]);

  const goNextScreen = useCallback(() => {
    if (activeScreen === 3) {
      if (commentValue.trim().length < MIN_COMMENT_LENGTH) {
        setIsCommentCritical(true);

        return;
      }

      setIsSubmitModalOpen(true);
      setActiveScreen((prev) => (prev + 1) as ActiveScreenType);

      return;
    }

    setActiveScreen((prev) => (prev + 1) as ActiveScreenType);
  }, [activeScreen, commentValue]);

  const goBackScreen = useCallback(() => {
    if (activeScreen === 1) {
      return;
    }

    setActiveScreen((prev) => (prev - 1) as ActiveScreenType);
  }, [activeScreen]);

  const handleSaveSimulation = useCallback(() => {
    Logger.log('Handle save Simulaton', isLoading);
  }, [isLoading]);

  return (
    <>
      {activeScreen <= 3 && (
        <ModalIsland
          isOpen={isOpen}
          onClose={handleModalClose}
          HeaderComp={
            <RemediationHeader
              subtitlelinkText={subtitlelinkText}
              handleClose={handleModalClose}
              redirectTo={redirectTo}
              onRedirect={onRedirect}
            />
          }
          isCloseButtonVisible={!isDisabled}
          className='Sod-RemediateActionModal'
          contentBody={
            <Box className='Sod-RemediateActionModal-bodyWrapper'>
              {activeScreen === 1 && (
                <RemediationFunctionScreen
                  sodKey={sodKey}
                  activeScreen={activeScreen}
                  functions={functions}
                  goNext={(keys) => {
                    setFnKeys(keys);
                    goNextScreen();
                  }}
                  setFunctionAnalysis={setFunctionAnalysis}
                  setIsSectionError={setIsSectionError}
                />
              )}
              {activeScreen === 2 && (
                <RemediationImpactScreen
                  sodKey={sodKey}
                  activeScreen={activeScreen}
                  functionAnalysis={functionAnalysis}
                  fnKeys={fnKeys}
                  functions={functions ?? []}
                  accounts={accounts ?? []}
                  userName={userName}
                  displayName={displayName}
                />
              )}
              {activeScreen === 3 && (
                <RemediationCommentScreen
                  isCommentCritical={isCommentCritical}
                  setIsCommentCritical={setIsCommentCritical}
                  commentValue={commentValue}
                  setCommentValue={setCommentValue}
                  setRemediateValue={setRemediateValue}
                />
              )}
            </Box>
          }
          FooterActionBarComp={
            <RemediationFooter
              activeScreen={activeScreen}
              isError={isSectionError}
              sectionRefIsScrolled={sectionRefIsScrolled}
              isButtonActive={commentValue.trim().length >= MIN_COMMENT_LENGTH}
              isRequest={!isRequest}
              remediateMode={
                functionAnalysis?.sameEntitlementsInAllFunctions
                  ? REMEDIATE_MODE.COM_ASG_ENTS
                  : REMEDIATE_MODE.MUL_FUN_ENTS
              }
              isAllowedSaveSimulation={isAllowedSaveSimulation ?? false}
              goNextScreen={goNextScreen}
              goBackScreen={goBackScreen}
              handleSaveSimulation={handleSaveSimulation}
            />
          }
        />
      )}
      {activeScreen === 4 && (
        <ModalIsland
          kind='warning'
          alertTitle={l(
            isRequest ? 'Submit Remediation Request?' : 'Start Remediation?'
          )}
          alertSubtitle={
            isRequest
              ? 'A request will be created to revoke access. The violation will be closed after the request is approved and the remediation task is successfully completed'
              : 'A remediation task will be created to revoke access. The violation will be closed once the task is successfully completed.'
          }
          onClose={handleModalClose}
          isOpen={isSubmitModalOpen}
          primaryButton={
            <Button
              type='button'
              kind='filled'
              size='medium'
              onClick={() => {
                handleSumbit(commentValue, {
                  functionId: Number(fnKeys?.[0]),
                  violationId: String(sodKey),
                  remediateOption: remediateValue,
                });
              }}
              isDisabled={isDisabled && !isLoading}
              isLoading={isLoading}
              loaderText={l('Confirming')}>
              {l('Confirm')}
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              isDisabled={isDisabled ?? isLoading}
              onClick={handleModalClose}>
              {l('Cancel')}
            </Button>
          }
        />
      )}
    </>
  );
}

ViolationsRemediateAction.defaultProps = {
  functions: [],
  accounts: [],
  isDisabled: false,
  isAllowedSaveSimulation: false,
  redirectTo: () => {},
  isDetailsPage: false,
};

export default memo(ViolationsRemediateAction);
